// @flow
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Button, { buttonConstants } from '@shared/Button';

import Checkbox from '@shared/Checkbox/component/Checkbox';
import AdvancedSelectField from '@shared/AdvancedSelectField';
import SearchField from '@search/components/SearchField';
import DateHelpers from '@helpers/DateHelpers';
import InputField from '_common/components/input-field/InputField';

import useActivities from '@activity/hooks/useActivities';

import type { SearchCity } from '@shared/Locations/types';
import type { Option } from '@core/types';
import type { GroupClassesFilterType } from '@groupClass/types';
import GroupClassesFilter from '@groupClass/components/GroupClassesFilter';
import { GROUP_CLASSES_FILTERS } from '@groupClass/constants';
import clsx from 'clsx';

type Props = {
  isAdmin: boolean,
  isManager: boolean,
  myClassesOnly: boolean,
  mySectorGroupClasses: boolean,
  activity: Option | null,
  caseNumber: string | null,
  day: Option | null,
  groupClassKey: string | null,
  value: SearchCity | null,
  groupClassesFilter: GroupClassesFilterType,
  locale: string,
  onChangeAddress: (event: SyntheticEvent<HTMLInputElement>) => void,
  onChangeCaseNumber: (event: SyntheticEvent<HTMLInputElement>) => void,
  onChangeGroupClassKey: (event: SyntheticEvent<HTMLInputElement>) => void,
  onChangeDay: () => void,
  setMyClassesOnly: (value: boolean) => void,
  setMySectorGroupClasses: (value: boolean) => void,
  onChangeActivity: () => void,
  onChangeGroupClassesFilter: () => void,
  onSubmit: (event: SyntheticEvent<EventTarget>) => void,
};

const SearchBanner = (props: Props): React$Node => {
  const {
    isAdmin,
    isManager,
    myClassesOnly,
    mySectorGroupClasses,
    onChangeAddress,
    onChangeCaseNumber,
    onChangeGroupClassKey,
    setMyClassesOnly,
    setMySectorGroupClasses,
    value,
    locale,
    activity,
    onChangeActivity,
    onChangeDay,
    day,
    groupClassKey,
    caseNumber,
    onSubmit,
    groupClassesFilter,
    onChangeGroupClassesFilter,
  } = props;

  const containerRef = useRef(null);
  const placeholderRef = useRef(null);
  const [isSticky, setSticky] = useState(false);
  const [height, setHeight] = useState(0);

  const { t, i18n: { language } } = useTranslation();
  const [ activities, isLoading ] = useActivities(language);

  const handleMySectorCheckbox = useCallback((): void => {
    setMySectorGroupClasses(!mySectorGroupClasses);
    if (isManager) {
      setMyClassesOnly(mySectorGroupClasses);
    }
  }, [mySectorGroupClasses, isManager]);

  const handleClassesOnly = useCallback((): void => {
    setMyClassesOnly(!myClassesOnly);
    if (isManager) {
      setMySectorGroupClasses(myClassesOnly);
    }
  }, [myClassesOnly, isManager]);

  useEffect(() => {
    const sidebar = containerRef.current;
    if (!sidebar) return;

    let originalOffset = 0;
    let lastScrollY = window.scrollY;

    const onResize = () => {
      if (sidebar) {
        originalOffset = sidebar.getBoundingClientRect().top + window.scrollY;
        setHeight(sidebar.offsetHeight);
      }
    };

    const onScroll = () => {
      const currentScrollY = window.scrollY;
      const scrollingDown = currentScrollY > lastScrollY;
      const scrollingUp = currentScrollY < lastScrollY;

      if (scrollingDown && currentScrollY >= originalOffset + height) {
        setSticky(true);
      }

      if (scrollingUp && currentScrollY <= originalOffset) {
        setSticky(false);
      }

      lastScrollY = currentScrollY;
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    onResize();

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
    };
  }, [height]);

  const activityOptions = useMemo((): Option[] => (
    activities?.map((activity) => ({
      label: activity.name,
      value: activity.id.toString(),
    })) || []
  ), [activities]);

  const daysOptions = DateHelpers.getDaysOfWeekOptions(language);

  return (
    <>
      { !isAdmin && (
        <div className="search-banner">
          <Checkbox
            className="search-banner-checkbox"
            checked={ mySectorGroupClasses }
            onChange={ handleMySectorCheckbox }
            name="mySectorGroupClasses"
          >
            { t('groupClasses.mySectorGroupClasses') }
          </Checkbox>
          <Checkbox
            className="search-banner-checkbox"
            checked={ myClassesOnly }
            onChange={ handleClassesOnly }
            name="myClassesOnly"
          >
            { t('groupClasses.myClassesOnly') }
          </Checkbox>
        </div>
      ) }
      <GroupClassesFilter
        groupClassesFilter={ groupClassesFilter }
        onChangeGroupClassesFilter={ onChangeGroupClassesFilter }
      />
      { /* Prevent layout shift */ }
      { isSticky && <div ref={ placeholderRef } style={ { height } } /> }
      <div
        ref={ containerRef }
        className={ clsx('search-banner-search search-container', { sticky: isSticky }) }>
        {
          groupClassesFilter !== GROUP_CLASSES_FILTERS.ONLINE && (
            <SearchField locale={ locale } value={ value } onChange={ onChangeAddress } isAdmin={ isAdmin } onSearch={ onSubmit } />
          )
        }
        <AdvancedSelectField
          name="activity"
          placeholder={ t('groupClasses.activities') }
          options={ activityOptions }
          isClearable
          className="search-field-input"
          value={ activity }
          onChange={ onChangeActivity }
          selectAll={ t('common.allFem') }
          isDisabled={ isLoading }
          isSearchable={ false }
        />
        <AdvancedSelectField
          name="day"
          options={ daysOptions }
          placeholder={ t('groupClasses.groupClass.detailed.day') }
          isClearable
          value={ day }
          className="search-field-input"
          onChange={ onChangeDay }
          selectAll={ t('common.allMasc') }
          isSearchable={ false }
        />
        <InputField
          name="groupClassKey"
          value={ groupClassKey }
          onChange={ onChangeGroupClassKey }
          className="search-field-input"
          placeholder={ t('groupClasses.groupClass.detailed.groupClassKey') }
        />
        <InputField
          name="caseNumber"
          value={ caseNumber }
          onChange={ onChangeCaseNumber }
          className="search-field-input"
          placeholder={ t('groupClasses.groupClass.detailed.caseNumber') }
        />
        <Button type={ buttonConstants.PRIMARY } onClick={ onSubmit }>
          { t('common.search') }
        </Button>
      </div>
    </>
  );
};

export default SearchBanner;
